import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDownToLine, ArrowUpFromLine, FilePlus2, Loader2, Pause, Play, Plus, Save, Send, Trash2, Workflow } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const STATUS_VARIANTS = {
  draft: "border-zinc-600 text-zinc-400",
  active: "border-emerald-500/40 text-emerald-300",
  paused: "border-amber-500/40 text-amber-300",
  archived: "border-rose-500/40 text-rose-300",
};

const TRIGGERS = [
  "manual",
  "member_activated",
  "project_closed",
  "dispatch_upcoming",
  "magic_link_issued",
  "subscription_past_due",
  "subscription_cancelled",
];

function SequenceEditor({ sequence, templates, onSaved, onClose }) {
  const initial = sequence || {
    slug: "",
    name: "",
    description: "",
    trigger: "manual",
    status: "draft",
    allow_reenrollment: false,
    steps: [],
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const isNew = !sequence?.slug;

  const addStep = () => {
    setForm({
      ...form,
      steps: [
        ...(form.steps || []),
        { order_index: (form.steps || []).length, template_slug: templates[0]?.slug || "", channel: "email", delay_hours: 0, label: "" },
      ],
    });
  };

  const updateStep = (i, patch) => {
    const next = [...form.steps];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, steps: next });
  };

  const removeStep = (i) => {
    const next = form.steps.filter((_, idx) => idx !== i);
    setForm({ ...form, steps: next });
  };

  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.steps.length) return;
    const next = [...form.steps];
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, steps: next });
  };

  const save = async () => {
    if (!form.slug || !form.name) { toast.error("Slug and name required"); return; }
    if ((form.steps || []).length === 0) { toast.error("Add at least one step"); return; }
    setBusy(true);
    try {
      await crmApi.upsertSequence(form.slug, {
        name: form.name,
        description: form.description,
        trigger: form.trigger,
        status: form.status,
        allow_reenrollment: !!form.allow_reenrollment,
        steps: form.steps.map((s, i) => ({ ...s, order_index: i, delay_hours: Number(s.delay_hours || 0) })),
      });
      toast.success(`Sequence “${form.name}” saved`);
      onSaved?.();
      onClose();
    } catch (exc) {
      toast.error(exc.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-black border-zinc-800 text-white max-w-3xl" data-testid="sequence-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "New sequence" : `Edit · ${form.name}`}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Slug</Label>
            <Input value={form.slug} disabled={!isNew} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-") })} className="bg-zinc-950 border-zinc-700 text-white font-mono" data-testid="seq-slug" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="seq-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Trigger</Label>
            <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="seq-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="seq-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">draft</SelectItem>
                <SelectItem value="active">active</SelectItem>
                <SelectItem value="paused">paused</SelectItem>
                <SelectItem value="archived">archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white">Steps</div>
            <Button size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" onClick={addStep} data-testid="add-step">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add step
            </Button>
          </div>
          <div className="space-y-2">
            {(form.steps || []).map((step, i) => (
              <div key={i} className="rounded-lg border border-zinc-800 bg-black/50 p-3 grid sm:grid-cols-[1fr,140px,120px,auto] gap-2 items-center" data-testid={`step-${i}`}>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase text-zinc-500">#{i + 1} · Template</div>
                  <Select value={step.template_slug} onValueChange={(v) => updateStep(i, { template_slug: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid={`step-${i}-template`}><SelectValue /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase text-zinc-500">Delay (hrs)</div>
                  <Input type="number" value={step.delay_hours} onChange={(e) => updateStep(i, { delay_hours: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid={`step-${i}-delay`} />
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase text-zinc-500">Channel</div>
                  <Select value={step.channel || "email"} onValueChange={(v) => updateStep(i, { channel: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid={`step-${i}-channel`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">email</SelectItem>
                      <SelectItem value="sms">sms</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-1 items-end justify-end pb-0.5">
                  <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => moveStep(i, -1)} disabled={i === 0}><ArrowUpFromLine className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1}><ArrowDownToLine className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => removeStep(i)} data-testid={`step-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
          <Button variant="ghost" onClick={onClose} className="text-zinc-300" disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="seq-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save sequence</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ sequences, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ sequence_slug: "", recipient_email: "", recipient_name: "", context: "" });

  const submit = async () => {
    if (!form.sequence_slug || !form.recipient_email) { toast.error("Sequence and email required"); return; }
    let ctx = {};
    if (form.context.trim()) {
      try { ctx = JSON.parse(form.context); }
      catch (_e) { toast.error("Context must be valid JSON"); return; }
    }
    setBusy(true);
    try {
      await crmApi.createEnrollment({
        sequence_slug: form.sequence_slug,
        recipient_email: form.recipient_email,
        recipient_name: form.recipient_name,
        context: ctx,
      });
      toast.success("Enrollment created");
      onSaved?.();
      setOpen(false);
    } catch (exc) {
      toast.error(exc.message || "Enroll failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900" data-testid="open-enroll">
          <Send className="h-4 w-4 mr-1" /> Manual enroll
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white max-w-lg">
        <DialogHeader><DialogTitle>Manual enrollment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Sequence</Label>
            <Select value={form.sequence_slug} onValueChange={(v) => setForm({ ...form, sequence_slug: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="enroll-sequence"><SelectValue placeholder="Pick a sequence" /></SelectTrigger>
              <SelectContent>{sequences.filter((s) => s.status === "active").map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Recipient email</Label>
            <Input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="enroll-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Recipient name</Label>
            <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Context (JSON)</Label>
            <Textarea rows={4} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white font-mono text-xs" data-testid="enroll-context" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-300" disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="confirm-enroll">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enroll"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SequencesView() {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [seqs, tpls, enrolls] = await Promise.all([
          crmApi.listSequences(),
          crmApi.listTemplates({ status: "active" }),
          crmApi.listEnrollments({ limit: 100 }),
        ]);
        if (!active) return;
        setSequences(seqs || []);
        setTemplates(tpls || []);
        setEnrollments(enrolls || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load sequences");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [counter]);

  const reload = useCallback(() => setCounter((c) => c + 1), []);

  const toggleStatus = async (seq) => {
    const next = seq.status === "active" ? "paused" : "active";
    try {
      await crmApi.upsertSequence(seq.slug, { status: next });
      toast.success(`Sequence ${next}`);
      reload();
    } catch (exc) {
      toast.error(exc.message || "Toggle failed");
    }
  };

  const pauseEnrollment = async (e) => {
    try {
      await crmApi.updateEnrollment(e.id, { status: e.status === "active" ? "paused" : "active" });
      reload();
    } catch (exc) {
      toast.error(exc.message || "Update failed");
    }
  };

  if (loading) {
    return <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading sequences…</div>;
  }

  return (
    <div className="space-y-6" data-testid="owner-sequences">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Workflow className="h-5 w-5 text-amber-300" /> Sequences</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">Multi-step nurture drips. Triggers auto-enroll the right recipient; manual enroll for tests.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <EnrollDialog sequences={sequences} onSaved={reload} />
          <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400" onClick={() => setEditing({})} data-testid="new-sequence-btn">
            <FilePlus2 className="h-4 w-4 mr-1" /> New sequence
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sequences-grid">
        {sequences.map((s) => (
          <Card key={s.id} className="bg-black/40 border-zinc-800" data-testid={`sequence-card-${s.slug}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-white font-semibold">{s.name}</div>
                  <div className="text-[11px] font-mono text-zinc-500">{s.slug}</div>
                </div>
                <Badge variant="outline" className={STATUS_VARIANTS[s.status] || ""}>{s.status}</Badge>
              </div>
              <div className="text-xs text-zinc-400 line-clamp-2">{s.description || "—"}</div>
              <div className="text-[11px] text-zinc-500">trigger: <span className="text-amber-300">{s.trigger}</span> · {s.steps?.length || 0} steps</div>
              <div className="flex flex-wrap gap-1 pt-1">
                <Button size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10" onClick={() => setEditing(s)} data-testid={`edit-sequence-${s.slug}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="text-white" onClick={() => toggleStatus(s)} data-testid={`toggle-sequence-${s.slug}`}>
                  {s.status === "active" ? <><Pause className="h-3.5 w-3.5 mr-1" /> Pause</> : <><Play className="h-3.5 w-3.5 mr-1" /> Activate</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-black/40 border-zinc-800" data-testid="enrollments-card">
        <CardContent className="p-4">
          <div className="text-sm text-white mb-3">Active enrollments ({enrollments.length})</div>
          {enrollments.length === 0 ? (
            <p className="text-xs text-zinc-500 py-4 text-center">No active enrollments. The system auto-enrolls members on welcome and project close.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-zinc-500 border-b border-zinc-800">
                  <tr><th className="text-left py-2 px-2">Sequence</th><th className="text-left py-2 px-2">Recipient</th><th className="text-left py-2 px-2">Status</th><th className="text-left py-2 px-2">Step</th><th className="text-left py-2 px-2">Next run</th><th className="text-right py-2 px-2">Actions</th></tr>
                </thead>
                <tbody>
                  {enrollments.map((e) => (
                    <tr key={e.id} className="border-b border-zinc-900" data-testid={`enrollment-row-${e.id}`}>
                      <td className="py-2 px-2"><div className="text-white">{e.sequence_name}</div><div className="text-[10px] font-mono text-zinc-500">{e.sequence_slug}</div></td>
                      <td className="py-2 px-2 text-zinc-300">{e.recipient_email}<div className="text-[10px] text-zinc-500">{e.recipient_name}</div></td>
                      <td className="py-2 px-2"><Badge variant="outline" className={STATUS_VARIANTS[e.status] || "border-zinc-700 text-zinc-300"}>{e.status}</Badge></td>
                      <td className="py-2 px-2 text-zinc-300">{e.current_step_index + 1} · sends: {e.sends_count}</td>
                      <td className="py-2 px-2 text-zinc-400 text-[11px]">{e.next_run_at ? new Date(e.next_run_at).toLocaleString() : "—"}</td>
                      <td className="py-2 px-2 text-right">
                        {["active", "paused"].includes(e.status) && (
                          <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => pauseEnrollment(e)} data-testid={`toggle-enrollment-${e.id}`}>
                            {e.status === "active" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {editing !== null && (
        <SequenceEditor sequence={editing.slug ? editing : null} templates={templates} onSaved={reload} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
